import { useCallback, useEffect, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { NavigationCustomizationItemDto } from '@asset-metrix/portal-gateway-client-js';
import { Button, isObject } from '@asset-metrix/portal-ui';

import { extractErrorMessage } from '../../utils/errorMessage';
import { useDeleteNavigationCustomizationMutation } from '../navigation/useDeleteNavigationCustomizationMutation';

import styles from './pageErrorFallback.module.scss';

export type PageErrorFallbackProps = {
  activeDynamicNavigationItem?: NavigationCustomizationItemDto;
  slug?: string;
  error: unknown;
};

export function PageErrorFallback({
  activeDynamicNavigationItem,
  slug,
  error,
}: PageErrorFallbackProps) {
  const { resetBoundary } = useErrorBoundary();
  const [storedSlug] = useState(slug);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const deleteNavigationCustomizationMutation =
    useDeleteNavigationCustomizationMutation();

  const handleDeleteNavigationCustomization = useCallback(() => {
    if (activeDynamicNavigationItem?.id) {
      deleteNavigationCustomizationMutation.mutate({
        itemId: activeDynamicNavigationItem?.id,
      });
      navigate(activeDynamicNavigationItem?.originHref ?? '/', {
        replace: true,
      });
    }
  }, [
    activeDynamicNavigationItem,
    deleteNavigationCustomizationMutation,
    navigate,
  ]);

  useEffect(() => {
    if (slug !== storedSlug) {
      resetBoundary();
    }
  }, [slug, storedSlug, resetBoundary]);

  const navigateToDashboard = () => {
    navigate('/');
  };

  const hasCause =
    (error instanceof Error || isObject(error)) && 'cause' in error;

  return (
    <div>
      <div className={styles['root']} data-testid="page-error-fallback">
        <h1>{t('app.page.notLoadedTitle')}</h1>
        <div className={styles['content']}>
          <span>{t('app.page.notLoadedErrorText')}</span>
          <p>{extractErrorMessage(error, t('app.page.genericError'))}</p>
          {hasCause && (
            <p>
              <code>{extractErrorMessage(error.cause)}</code>
            </p>
          )}
        </div>
        <div className={styles['buttons']}>
          {activeDynamicNavigationItem && (
            <Button
              enlarge
              onClick={handleDeleteNavigationCustomization}
              variant="primary"
            >
              {t('app.page.close')}
            </Button>
          )}
          <Button enlarge onClick={navigateToDashboard} variant="primary">
            {t('app.page.backToDashboard')}
          </Button>
        </div>
      </div>
    </div>
  );
}
